import React from 'react';
import './EnhancedInspectionReport.css';

interface InspectionBadge {
  label: string;
  value: string | number | boolean;
  status: 'excellent' | 'good' | 'warning' | 'danger' | 'neutral';
  icon: string;
  subtitle?: string;
  confidence?: number;
}

interface InspectionMetrics {
  overallHealth: number; // 0-100
  populationStrength: number; // 0-100
  broodQuality: number; // 0-100
  diseaseRisk: number; // 0-100
  honeyStores: number; // 0-100
}

interface AIFinding {
  category: string;
  finding: string;
  confidence: number;
  severity: 'low' | 'medium' | 'high';
  recommendation?: string;
  imageRef?: string;
}

interface EnhancedInspectionReportProps {
  date: string;
  hiveId: string;
  hiveName: string;
  badges: InspectionBadge[];
  metrics: InspectionMetrics;
  aiFindings?: AIFinding[];
  photos?: Array<{ url: string; analysis?: string }>;
  notes?: string;
  weather?: {
    condition: string;
    temperature: number;
    icon: string;
  };
}

const EnhancedInspectionReport: React.FC<EnhancedInspectionReportProps> = ({
  date,
  hiveId,
  hiveName,
  badges,
  metrics,
  aiFindings = [],
  photos = [],
  notes,
  weather
}) => {
  const getScoreColor = (score: number): string => {
    if (score >= 80) return '#27ae60'; // Green
    if (score >= 60) return '#f39c12'; // Yellow
    if (score >= 40) return '#e67e22'; // Orange
    return '#e74c3c'; // Red
  };

  const getBadgeClassName = (status: string): string => {
    return `inspection-badge inspection-badge--${status}`;
  };

  const renderCircularProgress = (score: number, label: string) => (
    <div className="circular-progress">
      <div className="circular-progress-inner">
        <svg className="circular-progress-svg" viewBox="0 0 36 36">
          <path
            className="circular-progress-bg"
            d="M18 2.0845
              a 15.9155 15.9155 0 0 1 0 31.831
              a 15.9155 15.9155 0 0 1 0 -31.831"
          />
          <path
            className="circular-progress-bar"
            d="M18 2.0845
              a 15.9155 15.9155 0 0 1 0 31.831
              a 15.9155 15.9155 0 0 1 0 -31.831"
            style={{
              strokeDasharray: `${score}, 100`,
              stroke: getScoreColor(score)
            }}
          />
        </svg>
        <div className="circular-progress-text">
          <span className="score">{score}</span>
          <span className="label">{label}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="enhanced-inspection-report">
      {/* Header Section */}
      <div className="inspection-header">
        <div className="inspection-title">
          <h2>🔍 Inspection Report</h2>
          <div className="inspection-meta">
            <span className="hive-info">🏺 {hiveName} ({hiveId})</span>
            <span className="date-info">📅 {new Date(date).toLocaleDateString()}</span>
            {weather && (
              <span className="weather-info">
                {weather.icon} {weather.condition}, {weather.temperature}°C
              </span>
            )}
          </div>
        </div>
        <div className="overall-score">
          {renderCircularProgress(metrics.overallHealth, 'Overall Health')}
        </div>
      </div>

      {/* Key Metrics Dashboard */}
      <div className="metrics-dashboard">
        <h3>📊 Health Metrics</h3>
        <div className="metrics-grid">
          <div className="metric-card">
            {renderCircularProgress(metrics.populationStrength, 'Population')}
            <span className="metric-label">Population Strength</span>
          </div>
          <div className="metric-card">
            {renderCircularProgress(metrics.broodQuality, 'Brood')}
            <span className="metric-label">Brood Quality</span>
          </div>
          <div className="metric-card">
            {renderCircularProgress(100 - metrics.diseaseRisk, 'Health')}
            <span className="metric-label">Disease Risk</span>
          </div>
          <div className="metric-card">
            {renderCircularProgress(metrics.honeyStores, 'Stores')}
            <span className="metric-label">Honey Stores</span>
          </div>
        </div>
      </div>

      {/* Inspection Badges */}
      <div className="badges-section">
        <h3>🏷️ Key Findings</h3>
        <div className="badges-grid">
          {badges.map((badge, index) => (
            <div key={index} className={getBadgeClassName(badge.status)}>
              <div className="badge-icon">{badge.icon}</div>
              <div className="badge-content">
                <div className="badge-label">{badge.label}</div>
                <div className="badge-value">
                  {typeof badge.value === 'boolean' 
                    ? (badge.value ? '✓ Yes' : '✗ No')
                    : badge.value
                  }
                </div>
                {badge.subtitle && (
                  <div className="badge-subtitle">{badge.subtitle}</div>
                )}
                {badge.confidence && (
                  <div className="badge-confidence">
                    Confidence: {badge.confidence > 1 ? Math.round(badge.confidence) : Math.round(badge.confidence * 100)}%
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI Findings */}
      {aiFindings.length > 0 && (
        <div className="ai-findings-section">
          <h3>🤖 AI Analysis Results</h3>
          <div className="ai-findings-list">
            {aiFindings.map((finding, index) => (
              <div key={index} className={`ai-finding ai-finding--${finding.severity}`}>
                <div className="ai-finding-header">
                  <span className="ai-finding-category">{finding.category}</span>
                  <span className="ai-finding-confidence">
                    {finding.confidence > 1 ? Math.round(finding.confidence) : Math.round(finding.confidence * 100)}% confidence
                  </span>
                </div>
                <div className="ai-finding-content">
                  <p className="ai-finding-text">{finding.finding}</p>
                  {finding.recommendation && (
                    <div className="ai-recommendation">
                      <strong>💡 Recommendation:</strong> {finding.recommendation}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Photo Analysis */}
      {photos.length > 0 && (
        <div className="photos-section">
          <h3>📸 Photo Analysis</h3>
          <div className="photos-grid">
            {photos.map((photo, index) => (
              <div key={index} className="photo-analysis-card">
                <img src={photo.url} alt={`Inspection ${index + 1}`} />
                {photo.analysis && (
                  <div className="photo-analysis-text">
                    <p>{photo.analysis}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes Section */}
      {notes && (
        <div className="notes-section">
          <h3>📝 Inspector Notes</h3>
          <div className="notes-content">
            <pre>{notes}</pre>
          </div>
        </div>
      )}

      {/* Action Items */}
      <div className="action-items-section">
        <h3>✅ Recommended Actions</h3>
        <div className="action-items">
          {metrics.diseaseRisk > 30 && (
            <div className="action-item action-item--urgent">
              <span className="action-icon">⚠️</span>
              <span className="action-text">Monitor for disease symptoms closely</span>
            </div>
          )}
          {metrics.broodQuality < 60 && (
            <div className="action-item action-item--important">
              <span className="action-icon">🍯</span>
              <span className="action-text">Consider supplemental feeding</span>
            </div>
          )}
          {metrics.populationStrength > 80 && (
            <div className="action-item action-item--normal">
              <span className="action-icon">📦</span>
              <span className="action-text">Consider adding super for honey flow</span>
            </div>
          )}
          <div className="action-item action-item--routine">
            <span className="action-icon">📅</span>
            <span className="action-text">Schedule next inspection in 7-14 days</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedInspectionReport;